import { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { 
  REMINDER_HOURS_MAP, 
  CHECK_IN_HOURS_MAP, 
  REMINDER_SEQUENCE, 
  CHECK_IN_SEQUENCE 
} from '../config/notificationConfig';

interface UseNotificationTimingReturn {
  timeLeft: string | null;
  timeLeftOneHour: string | null;
  isLessThan24Hours: boolean;
  noTimeLeft: boolean;
  isMissed: boolean;
  timeLeftUTC: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
}

export const useNotificationTiming = (
  item: any,
  selectedButton: string
): UseNotificationTimingReturn => {
  const [timeLeft, setTimeLeft] = useState<string | null>(null);
  const [timeLeftOneHour, setTimeLeftOneHour] = useState<string | null>(null);
  const [isLessThan24Hours, setIsLessThan24Hours] = useState(false);
  const [noTimeLeft, setNoTimeLeft] = useState(false);
  const [isMissed, setIsMissed] = useState(false);
  const [timeLeftUTC, setTimeLeftUTC] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  
  // Add error handling for invalid dates
  const isValidEventTime = useMemo(() => {
    if (!item?.details?.eventStartTime) return false;
    const date = new Date(item.details.eventStartTime);
    return !isNaN(date.getTime());
  }, [item?.details?.eventStartTime]);

  // Memoized helper function to get next hour in sequence
  const getNextHour = useMemo(() => {
    return (h: number, sequence: number[]) => {
      const index = sequence.indexOf(h);
      if (index !== -1 && index < sequence.length - 1) {
        return sequence[index + 1];
      }
      return 0;
    };
  }, []);

  // Calculate initial missed status
  const calculateInitialMissedStatus = useMemo(() => {
    return () => {
      if (
        item.actionType === 'attendance-reminder' ||
        item.actionType === 'check-in'
      ) {
        if (item.actionStatus === 'pending') {
          const now = moment.utc();
          const start = moment.utc(item?.details?.eventStartTime);
          const hoursLeft = moment.duration(start.diff(now)).asHours();
          
          let h: number;
          let sequence: number[];
          
          if (item.actionType === 'attendance-reminder') {
            h = REMINDER_HOURS_MAP[item.notificationKey as keyof typeof REMINDER_HOURS_MAP];
            sequence = REMINDER_SEQUENCE;
          } else {
            h = CHECK_IN_HOURS_MAP[item.category as keyof typeof CHECK_IN_HOURS_MAP];
            sequence = CHECK_IN_SEQUENCE;
          }
          
          if (h !== undefined) {
            const nextH = getNextHour(h, sequence);
            return hoursLeft < nextH;
          }
        }
      } else if (item.actionType === 'attendance-missed') {
        const now = moment.utc();
        const start = moment.utc(item?.details?.eventStartTime);
        const hoursLeft = moment.duration(start.diff(now)).asHours();
        return hoursLeft <= 0;
      }
      return false;
    };
  }, [item, getNextHour]);

  // Main countdown effect
  useEffect(() => {
    if (!isValidEventTime) {
      console.warn('Invalid event time, skipping timing calculations');
      setTimeLeft(null);
      setTimeLeftOneHour(null);
      setNoTimeLeft(true);
      setIsMissed(false);
      return;
    }
    
    // Set initial missed status
    setIsMissed(calculateInitialMissedStatus());

    const interval = setInterval(() => {
      try {
        const now = moment.utc();
        const start = moment.utc(item?.details?.eventStartTime);
        
        if (!start.isValid()) {
          console.warn('Invalid start time in interval');
          clearInterval(interval);
          return;
        }
        
        const duration = moment.duration(start.diff(now));

        if (duration.asSeconds() <= 0) {
          clearInterval(interval);
          setTimeLeft('00:00:00');
          setTimeLeftOneHour(null);
          setNoTimeLeft(true);
          return;
        }

        const hoursLeft = duration.asHours();

        // Handle different countdown displays based on selectedButton
        if (hoursLeft < 24 && selectedButton !== 'Im Watching') {
          const hours = Math.floor(hoursLeft).toString().padStart(2, '0');
          const minutes = Math.floor(duration.minutes()).toString().padStart(2, '0');
          const seconds = Math.floor(duration.seconds()).toString().padStart(2, '0');
          
          setIsLessThan24Hours(hoursLeft <= 24);
          setTimeLeft(`${hours}:${minutes}:${seconds}`);
        } else if (selectedButton === 'Im Watching' && hoursLeft < 1) {
          const hours = Math.floor(hoursLeft).toString().padStart(2, '0');
          const minutes = Math.floor(duration.minutes()).toString().padStart(2, '0');
          const seconds = Math.floor(duration.seconds()).toString().padStart(2, '0');
          
          setTimeLeftOneHour(`${hours}:${minutes}:${seconds}`);
        } else {
          setTimeLeft(null);
          setTimeLeftOneHour(null);
        }

        // Update missed status for attendance reminders and check-ins
        if (
          (item.actionType === 'attendance-reminder' ||
            item.actionType === 'check-in') &&
          item.actionStatus === 'pending'
        ) {
          let h: number;
          let sequence: number[];
          
          if (item.actionType === 'attendance-reminder') {
            h = REMINDER_HOURS_MAP[item.notificationKey as keyof typeof REMINDER_HOURS_MAP];
            sequence = REMINDER_SEQUENCE;
          } else {
            h = CHECK_IN_HOURS_MAP[item.category as keyof typeof CHECK_IN_HOURS_MAP];
            sequence = CHECK_IN_SEQUENCE;
          }
          
          if (h !== undefined) {
            const nextH = getNextHour(h, sequence);
            setIsMissed(hoursLeft < nextH);
          }
        } else if (item.actionType === 'attendance-missed') {
          const hoursLeft = moment.duration(start.diff(now)).asHours();
          setIsMissed(hoursLeft <= 0);
        }
      } catch (error) {
        console.error('Error in timing calculation:', error);
        clearInterval(interval);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [item, selectedButton, calculateInitialMissedStatus, getNextHour, isValidEventTime]);

  // UTC time calculation effect
  useEffect(() => {
    if (!isValidEventTime) {
      setTimeLeftUTC({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      setNoTimeLeft(true);
      return;
    }
    
    const calculateTimeLeft = () => {
      try {
        if (item?.details?.eventStartTime) {
          const eventDate = new Date(item.details.eventStartTime);
          
          if (isNaN(eventDate.getTime())) {
            console.warn('Invalid event date in UTC calculation');
            return;
          }
          
          const now = Date.now();
          const difference = eventDate.getTime() - now;
          
          if (difference > 0) {
            setTimeLeftUTC({
              days: Math.floor(difference / (1000 * 60 * 60 * 24)),
              hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
              minutes: Math.floor((difference / 1000 / 60) % 60),
              seconds: Math.floor((difference / 1000) % 60),
            });
          } else {
            setTimeLeftUTC({ days: 0, hours: 0, minutes: 0, seconds: 0 });
            setNoTimeLeft(true);
          }
        }
      } catch (error) {
        console.error('Error in UTC time calculation:', error);
      }
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);
    
    return () => {
      clearInterval(interval);
    };
  }, [item, isValidEventTime]);

  return {
    timeLeft,
    timeLeftOneHour,
    isLessThan24Hours,
    noTimeLeft,
    isMissed,
    timeLeftUTC,
  };
};
